import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/apiConfig';

const MenuBoard = () => {
    const { slug } = useParams();
    const [paginas, setPaginas] = useState([]);
    const [indiceActual, setIndiceActual] = useState(0);
    const [localInfo, setLocalInfo] = useState({ nombre: "CARGANDO...", color: "#0056b3" });
    const [loading, setLoading] = useState(true);
    
    // Estados para el tiempo de rotación
    const [tiempoElegido, setTiempoElegido] = useState(null); // en milisegundos

    const fetchData = useCallback(async () => {
        try {
            const response = await axios.get(`${config.api_url}/api/productos/catalogo/publico/${slug}`);
            if (response.data) {
                const dataRaw = response.data.productos || response.data;
                const todosLosProductos = Array.isArray(dataRaw) ? dataRaw : [];
                setLocalInfo({
                    nombre: response.data.tienda_nombre || response.data.nombre_negocio || "KOMITY",
                    color: response.data.color_primario || "#0056b3"
                });

                const itemsPorPagina = 12;
                const paginasAgrupadas = [];
                for (let i = 0; i < todosLosProductos.length; i += itemsPorPagina) {
                    paginasAgrupadas.push(todosLosProductos.slice(i, i + itemsPorPagina));
                }
                setPaginas(paginasAgrupadas);
                setLoading(false);
            }
        } catch (error) {
            console.error("Error:", error);
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchData();
        const autoRefresh = setInterval(fetchData, 300000);
        return () => clearInterval(autoRefresh);
    }, [fetchData]);

    // Lógica del Slide basada en el tiempo elegido
    useEffect(() => {
        if (paginas.length > 1 && tiempoElegido) {
            const timer = setInterval(() => {
                setIndiceActual((prev) => (prev + 1) % paginas.length);
            }, tiempoElegido); 
            return () => clearInterval(timer);
        }
    }, [paginas, tiempoElegido]);

    if (loading) return <div className="loading-screen"><h2>CARGANDO SISTEMA...</h2></div>;

    // PANTALLA DE SELECCIÓN DE TIEMPO (Solo se muestra si no hay tiempoElegido)
    if (!tiempoElegido) {
        return (
            <div className="setup-overlay" style={{ backgroundColor: '#000' }}>
                <div className="setup-card">
                    <h1 className="text-warning mb-4">CONFIGURACIÓN DE PANTALLA</h1>
                    <p className="text-white h4 mb-5">Seleccione el tiempo de rotación para las páginas:</p>
                    <div className="options-grid">
                        {[15, 30, 45, 60].map(seg => (
                            <button 
                                key={seg} 
                                className="btn-setup" 
                                onClick={() => setTiempoElegido(seg * 1000)}
                            >
                                {seg} SEGUNDOS
                            </button>
                        ))}
                    </div>
                    <p className="mt-5 text-muted small">KOMITY DIGITAL BOARD SYSTEM v2.0</p>
                </div>
            </div>
        );
    }

    const productosPagina = paginas[indiceActual] || [];
    const categorias = [...new Set(productosPagina.map(p => p.categoria_nombre))];

    return (
        <div className="board-wrapper">
            <header className="board-header" style={{ backgroundColor: localInfo.color }}>
                <h1 className="local-name">{localInfo.nombre}</h1>
                <div className="page-info">VALORES - PÁG {indiceActual + 1} / {paginas.length}</div>
            </header>

            <main className="board-body slide-fade" key={indiceActual}>
                {categorias.map(cat => (
                    <div key={cat} className="cat-group">
                        <h2 className="cat-title">{cat}</h2>
                        {productosPagina.filter(p => p.categoria_nombre === cat).map(prod => (
                            <div key={prod.id} className="item-row">
                                <div className="item-left">
                                    <div className="item-name">{prod.nombre}</div>
                                    <div className="item-desc">{prod.descripcion}</div>
                                </div>
                                <div className="item-value">
                                    ${Number(prod.valor || 0).toLocaleString('es-CL')}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </main>

            <footer className="board-footer">
                <div className="progress-bg">
                    <div 
                        className="progress-fill" 
                        key={indiceActual}
                        style={{ animationDuration: `${tiempoElegido}ms` }}
                    ></div>
                </div>
            </footer>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&display=swap');
                * { box-sizing: border-box; }
                html, body { margin: 0; padding: 0; background: black; overflow: hidden; height: 100vh; font-family: 'Oswald', sans-serif; }

                /* SETUP OVERLAY */
                .setup-overlay {
                    position: fixed; top:0; left:0; width:100vw; height:100vh;
                    display: flex; align-items: center; justify-content: center; z-index: 9999;
                }
                .setup-card { text-align: center; padding: 50px; border: 2px solid #333; border-radius: 20px; width: 80%; max-width: 800px; }
                .options-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .btn-setup {
                    background: transparent; border: 2px solid #ffc107; color: #ffc107;
                    padding: 30px; font-size: 2rem; font-family: 'Oswald'; cursor: pointer;
                    transition: 0.3s; border-radius: 10px; font-weight: 700;
                }
                .btn-setup:hover { background: #ffc107; color: black; transform: scale(1.05); }

                /* BOARD STYLES */
                .board-wrapper { display: flex; flex-direction: column; height: 100vh; color: white; }
                .board-header { height: 15vh; display: flex; justify-content: space-between; align-items: center; padding: 0 50px; }
                .local-name { font-size: 3.5rem; margin: 0; text-transform: uppercase; font-weight: 700; }
                .page-info { font-size: 1.5rem; opacity: 0.8; }

                .board-body { height: 82vh; padding: 40px 50px; overflow: hidden; }
                .cat-title { font-size: 3rem; color: #ffc107; text-transform: uppercase; border-bottom: 4px solid #ffc107; margin-bottom: 20px; }
                .item-row { display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-bottom: 1px solid rgba(255,255,255,0.1); }
                .item-name { font-size: 2.2rem; font-weight: 700; text-transform: uppercase; }
                .item-desc { font-size: 1.2rem; color: #aaa; }
                .item-value { font-size: 2.5rem; font-weight: 700; color: #ffc107; }

                .board-footer { height: 3vh; background: #111; }
                .progress-bg { width: 100%; height: 100%; background: rgba(255,255,255,0.1); }
                .progress-fill { height: 100%; background: #ffc107; width: 0%; animation: fillProgress linear forwards; }

                @keyframes fillProgress { from { width: 0%; } to { width: 100%; } }
                .slide-fade { animation: fadeIn 0.8s ease-in-out; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .loading-screen { background: black; height: 100vh; display: flex; justify-content: center; align-items: center; color: white; }
            `}</style>
        </div>
    );
};

export default MenuBoard;